(() => {
  const VIDEO_SELECTOR = [
    '.m-product-card__video video',
    '.m-collection-banner__media video',
    '.slim-banner__inner video'
  ].join(',');

  const managedVideos = new WeakSet();
  const visibleVideos = new Set();

  const configureVideo = (video) => {
    video.autoplay = true;
    video.loop = true;
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;

    video.setAttribute('autoplay', '');
    video.setAttribute('loop', '');
    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');
    video.setAttribute('webkit-playsinline', '');
  };

  const attemptPlayback = (video) => {
    if (!video.isConnected || document.hidden) {
      return;
    }

    configureVideo(video);
    video.controls = false;
    video.removeAttribute('controls');

    const playPromise = video.play();

    if (!playPromise || typeof playPromise.then !== 'function') {
      return;
    }

    playPromise
      .then(() => {
        video.controls = false;
        video.removeAttribute('controls');
      })
      .catch(() => {
        if (video.readyState >= HTMLMediaElement.HAVE_CURRENT_DATA) {
          video.controls = true;
        }
      });
  };

  const visibilityObserver = new IntersectionObserver(
    (entries) => {
      entries.forEach((entry) => {
        const video = entry.target;

        if (entry.isIntersecting) {
          visibleVideos.add(video);
          attemptPlayback(video);
        } else {
          visibleVideos.delete(video);
          video.pause();
        }
      });
    },
    {
      rootMargin: '120px 0px',
      threshold: 0.05
    }
  );

  const prepareVideo = (video) => {
    if (managedVideos.has(video)) {
      return;
    }

    managedVideos.add(video);
    configureVideo(video);
    visibilityObserver.observe(video);

    video.addEventListener('loadedmetadata', () => {
      if (visibleVideos.has(video)) {
        attemptPlayback(video);
      }
    });

    video.addEventListener('canplay', () => {
      if (visibleVideos.has(video)) {
        attemptPlayback(video);
      }
    });
  };

  const scanForVideos = (scope = document) => {
    if (scope.matches?.(VIDEO_SELECTOR)) {
      prepareVideo(scope);
    }

    scope.querySelectorAll?.(VIDEO_SELECTOR).forEach(prepareVideo);
  };

  const retryVisibleVideos = () => {
    visibleVideos.forEach(attemptPlayback);
  };

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (
        mutation.type === 'attributes' &&
        mutation.target.matches?.(VIDEO_SELECTOR) &&
        mutation.attributeName === 'controls'
      ) {
        if (visibleVideos.has(mutation.target)) {
          attemptPlayback(mutation.target);
        }

        return;
      }

      mutation.addedNodes.forEach((node) => {
        if (node.nodeType === Node.ELEMENT_NODE) {
          scanForVideos(node);
        }
      });
    });
  });

  const initialize = () => {
    scanForVideos(document);

    mutationObserver.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['controls']
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initialize, {
      once: true
    });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => {
    scanForVideos(event.target);
  });

  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      retryVisibleVideos();
    }
  });

  window.addEventListener('pageshow', retryVisibleVideos);

  ['pointerdown', 'touchstart', 'keydown'].forEach((eventName) => {
    document.addEventListener(eventName, retryVisibleVideos, {
      once: true,
      passive: eventName !== 'keydown'
    });
  });
})();
